// shared/hooks/useNotifications.ts
import { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { useNotificationsStore } from '../stores/useNotificationsStore';
import { UserInfoNotification } from '../../types/notifications';
import { App as AntdApp } from 'antd';
import { getAndSanitize } from '../utils/sanitization';
import { NotificationStack } from '../../components/NotificationBell/NotificationStack';

export const useNotifications = (onOpenDrawer?: () => void) => {
  const { message } = AntdApp.useApp();
  const { onUserInfoNotification, offUserInfoNotification, onUserInfoNotificationResolved, offUserInfoNotificationResolved } = useSocket();
  const {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    addNotification,
    updateNotification,
    clearError
  } = useNotificationsStore();

  // State para manejar las notificaciones visibles en popups
  const [popupNotifications, setPopupNotifications] = useState<UserInfoNotification[]>([]);

  // Evitar duplicados de popup usando ref
  const shownNotifications = useRef<Set<string>>(new Set());

  // Obtener adminId del usuario actual
  const adminId = getAndSanitize('userId');

  // Refs para callbacks estables
  const adminIdRef = useRef(adminId);
  const messageRef = useRef(message);

  // Actualizar refs
  useEffect(() => {
    adminIdRef.current = adminId;
    messageRef.current = message;
  });

  // Cargar notificaciones al montar
  useEffect(() => {
    if (adminId) {
      fetchNotifications(adminId);
    }
  }, [adminId, fetchNotifications]);

  // Mostrar popup para nueva notificación
  const showNotificationPopup = useCallback((data: UserInfoNotification) => {
    // Evitar duplicados
    if (shownNotifications.current.has(data.id)) {
      return;
    }

    shownNotifications.current.add(data.id);

    // Agregar al state de popups
    setPopupNotifications(prev => [...prev, data]);

    // Limpiar el Set después de 10 segundos
    setTimeout(() => {
      shownNotifications.current.delete(data.id);
    }, 10000);
  }, []);

  // Manejar cierre de popup - STABLE
  const handleClosePopup = useCallback((notificationId: string) => {
    setPopupNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Limpiar todos los popups - STABLE
  const clearAllPopups = useCallback(() => {
    setPopupNotifications([]);
  }, []);

  // Manejar resolución desde popup - STABLE
  const handleResolveFromPopup = useCallback(async (notificationId: string) => {
    const { markAsResolved } = useNotificationsStore.getState();
    try {
      await markAsResolved(notificationId, adminIdRef.current || '');
      handleClosePopup(notificationId);
      messageRef.current.success('Notificación resuelta');
    } catch (error) {
      console.error('[useNotifications] Error resolving notification:', error);
      messageRef.current.error('Error al resolver la notificación');
    }
  }, [handleClosePopup]);

  // Manejar nueva notificación desde socket - STABLE
  const handleNewNotification = useCallback((data: UserInfoNotification) => {

    // Verificar que la notificación es para este admin
    if (data.adminId !== adminIdRef.current) {
      return;
    }

    // Agregar al store
    addNotification(data);

    // Mostrar popup
    showNotificationPopup(data);
  }, [addNotification, showNotificationPopup]);

  // Manejar notificación resuelta desde socket
  const handleNotificationResolved = useCallback((data: { id: string; status: string; resolvedAt: string }) => {

    // Actualizar en el store
    updateNotification(data.id, {
      status: 'RESOLVED',
      resolvedAt: data.resolvedAt
    });

    // Cerrar el popup si existe
    handleClosePopup(data.id);
  }, [updateNotification, handleClosePopup]);

  // Configurar listeners de socket
  useEffect(() => {
    onUserInfoNotification(handleNewNotification);
    onUserInfoNotificationResolved(handleNotificationResolved);

    // Cleanup
    return () => {
      offUserInfoNotification(handleNewNotification);
      offUserInfoNotificationResolved(handleNotificationResolved);
    };
  }, [
    onUserInfoNotification,
    offUserInfoNotification,
    onUserInfoNotificationResolved,
    offUserInfoNotificationResolved,
    handleNewNotification,
    handleNotificationResolved
  ]);

  // Memoizar el componente de stack para evitar re-renders
  const NotificationStackComponent = useMemo(
    () => (
      <NotificationStack
        notifications={popupNotifications}
        onResolve={handleResolveFromPopup}
        onClose={handleClosePopup}
        onOpenDrawer={onOpenDrawer}
      />
    ),
    [popupNotifications, handleResolveFromPopup, handleClosePopup, onOpenDrawer]
  );

  return {
    notifications,
    unreadCount,
    loading,
    error,
    clearError,
    refresh: () => adminId && fetchNotifications(adminId),
    popupNotifications,
    handleResolveFromPopup,
    handleClosePopup,
    clearAllPopups,
    NotificationStackComponent
  };
};
